import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from 'hooks/useCustomQuery';
import axios from 'axios';
import AGGridMain from 'components/tables/AGGrid/AGGridMain';
import { Box, IconButton, Tooltip } from '@chakra-ui/react';
import { BsThreeDotsVertical } from 'react-icons/bs';
import ItemCodeMappingForm from './ItemCodeMappingForm';

/**
 * Item Code Mappings component from the Plans comonent of TCAp
 */

const ItemCodeMappings = ({ child }) => {
    const tableRef = useRef();
    const [action, setAction] = useState('LIST'); // LIST | ADD | EDIT
    const [editData, setEditData] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [apiError, setApiError] = useState(null);

    // Fetch item code mappings data using TanStack Query
    const {
        data: mappingsData = [],
        isLoading,
        error,
        refetch,
    } = useQuery({
        queryKey: ['itemCodeMappings', child?.id],
        queryFn: () => fetchItemCodeMappings(child?.id),
        enabled: !!child?.id,
    });
    const { darkMode } = useSelector((state) => state.settings);
    // AGGrid columns
    const columns = [
        {
            headerName: 'Category',
            field: 'category',
            flex: 1,
            sort: 'asc',
            sortIndex: 0,
        },
        { headerName: 'Item Code', field: 'itemCode', flex: 1 },
        { headerName: 'Account Code', field: 'accountCode', flex: 1 },
        { headerName: 'Tracking Name 1', field: 'trackingName1', flex: 1 },
        { headerName: 'Tracking Option 1', field: 'trackingOption1', flex: 1 },
        {
            headerName: 'Action',
            lockPosition: 'right',
            width: 120,
            sortable: false,
            filter: false,
            suppressMovable: true,
            lockVisible: true,
            suppressColumnsToolPanel: true,
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
            enableRowGroup: false,
            resizable: false,
            cellStyle: () => ({
                display: 'flex',
                alignItems: 'center',
            }),
            cellRenderer: (params) => (
                <Tooltip label="Edit">
                    <IconButton
                        icon={
                            <BsThreeDotsVertical
                                fill={darkMode ? 'white' : 'black'}
                            />
                        }
                        aria-label="Edit"
                        variant="ghost"
                        onClick={() => {
                            setEditData(params.data);
                            setAction('EDIT');
                        }}
                        _hover={{
                            bg: darkMode ? 'whiteAlpha.200' : 'blackAlpha.100',
                        }}
                        _active={{
                            bg: darkMode ? 'whiteAlpha.300' : 'blackAlpha.200',
                        }}
                        _focus={{ boxShadow: 'none' }}
                    />
                </Tooltip>
            ),
        },
    ];

    // Handlers
    // const handleAdd = () => {
    //     setEditData(null);
    //     setAction('ADD');
    // };

    const handleFormSubmit = async (values) => {
        try {
            if (action === 'ADD') {
                const payload = Object.fromEntries(
                    Object.entries(values).filter(
                        ([, v]) => v !== '' && v !== null && v !== undefined,
                    ),
                );
                await axios.post('/itemcodemapping', {
                    ...payload,
                    planID: child?.id,
                    isActive: true,
                });
            } else if (action === 'EDIT') {
                await axios.put('/itemcodemapping', {
                    ...values,
                    id: editData.id,
                    planID: child?.id,
                });
            }
            setAction('LIST');
            setEditData(null);
            refetch();
        } catch (err) {
            setApiError(err?.response?.data?.message || 'Error saving data');
        } finally {
            setIsSaving(false);
        }
    };
    const handleCancel = () => {
        setAction('LIST');
        setEditData(null);
    };

    return (
        <Box style={{ minHeight: '400px', overflowY: 'auto' }}>
            {action === 'LIST' && (
                <>
                    <AGGridMain
                        gridRef={tableRef}
                        tableData={{ columns, data: mappingsData }}
                        refetch={refetch}
                        isLoading={isLoading}
                        titleName="Item Code Mapping"
                    />
                    {error && (
                        <Box color="red.500">
                            Error loading item code mappings data.
                        </Box>
                    )}
                </>
            )}
            {(action === 'ADD' || action === 'EDIT') && (
                <>
                    {apiError && (
                        <Box color="red.500" mb={2}>
                            {apiError}
                        </Box>
                    )}
                    <ItemCodeMappingForm
                        initialValues={
                            action === 'EDIT' && editData
                                ? {
                                      //category: editData.category || '',
                                      itemCode: editData.itemCode || '',
                                      accountCode: editData.accountCode || '',
                                      trackingName1:
                                          editData.trackingName1 || '',
                                      trackingOption1:
                                          editData.trackingOption1 || '',
                                  }
                                : {
                                      //category: '',
                                      itemCode: '',
                                      accountCode: '',
                                      trackingName1: '',
                                      trackingOption1: '',
                                  }
                        }
                        onSubmit={handleFormSubmit}
                        isSubmitting={isSaving}
                        mode={action === 'ADD' ? 'add' : 'edit'}
                        category={action === 'EDIT' ? editData?.category : ''}
                        darkMode={darkMode}
                        onBack={handleCancel}
                    />
                </>
            )}
        </Box>
    );
};

const fetchItemCodeMappings = async (planId) => {
    if (!planId) return [];
    const response = await axios.get(`/itemcodemappings/${planId}`);
    return response.data;
};

export default ItemCodeMappings;
